<script setup lang="ts">
import type { Order } from "@shopware-pwa/types";
import { computed } from "vue";
import { Divider } from "~/components/clarity-ui/Divider";
import { Tint } from "~/components/clarity-ui/Tint";
import { FormattedPercent } from "~/components/ui/FormattedPercent";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import SharedPrice from "~/templates/components/shop/shared/SharedPrice.vue";

export interface OrderLineItemsPriceProps {
  order?: Order;
}

const { order } = defineProps<OrderLineItemsPriceProps>();

const customFields = computed(() => order?.customFields);
const withPromotion = computed(() => customFields.value?.hasOwnProperty("promotions"));
const subtotal = computed(
  () =>
    customFields.value?.originalOrderPrices?.positionPrice ??
    customFields.value?.priceWithoutPromotion,
);
const discount = computed(() => customFields.value?.promotions?.[0]?.discount);
const shippingTotal = computed(
  () => customFields.value?.originalOrderPrices?.shippingCosts?.totalPrice ?? order?.shippingTotal,
);
const amountTotal = computed(
  () => customFields.value?.originalOrderPrices?.totalPrice ?? order?.amountTotal,
);
const amountNet = computed(
  () => customFields.value?.originalOrderPrices?.netPrice ?? order?.amountNet,
);
const taxes = computed(
  () => customFields.value?.originalOrderPrices?.totalTaxes ?? order?.price?.calculatedTaxes,
);
</script>

<template>
  <div v-if="order" class="xl:flex xl:justify-end">
    <div class="grid grid-cols-2 gap-1">
      <CommonLabel path="BWF/shop/account/orderHistory/positionPrice" />
      <SharedPrice
        :value="subtotal"
        class="justify-self-end"
        data-testid="cart-subtotal"
        has-asterisk
      />
      <CommonLabel v-if="withPromotion" path="BWF/shop/account/orderHistory/promotionLabel" />
      <SharedPrice v-if="withPromotion" :value="discount" class="justify-self-end"
        ><template #beforePrice>-</template></SharedPrice
      >
      <CommonLabel class="xl:text-base" path="BWF/shop/account/orderHistory/shippingCost" />
      <SharedPrice
        :value="shippingTotal"
        class="justify-self-end"
        data-testid="cart-subtotal"
        has-asterisk
      />

      <Divider class="col-span-full my-2" />

      <CommonLabel class="font-medium" path="BWF/shop/account/orderHistory/grandTotal" />
      <SharedPrice
        :value="amountTotal"
        class="justify-self-end font-medium"
        data-testid="cart-subtotal"
        has-asterisk
      />
      <Tint class="text-sm">
        <CommonLabel path="BWF/shop/account/orderHistory/totalNetSum" />
      </Tint>
      <Tint class="justify-self-end">
        <SharedPrice class="text-sm" :value="amountNet" data-testid="cart-subtotal" />
      </Tint>
      <div
        v-for="calculatedTax in taxes"
        :key="calculatedTax.taxRate"
        class="col-span-full grid grid-cols-2 gap-1"
      >
        <Tint class="text-sm">
          <CommonLabel path="/BWF/shop/checkout/includes" />&nbsp;<FormattedPercent
            :value="calculatedTax?.taxRate"
            :precision="0"
          />&nbsp;<CommonLabel path="/BWF/shop/checkout/tax" />
        </Tint>
        <Tint class="place-self-end">
          <SharedPrice v-if="calculatedTax?.tax" :value="calculatedTax?.tax" class="text-sm" />
        </Tint>
      </div>
    </div>
  </div>
</template>
